import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import { idempotencyKeyFor } from '@/lib/http-core';
import type { EventWinnerRequest, EventWinnerView } from '../types/cms';

// C-8: 당첨자 발표 게시글 CRUD. BE create = POST /admin/v1/events/{eventId}/winners.
// 목록/수정/삭제 엔드포인트는 be-catalog 와 계약 확정 중 — 합의 경로로 정렬.
const base = (eventId: string) => `/admin/v1/events/${eventId}/winners`;

interface Envelope<T> {
  success?: boolean;
  data?: T | null;
}

/** 목록 요약 행(본문·번역 없음). 본문은 글을 열 때 단건 GET 으로 받는다. */
export interface EventWinnerSummary {
  winnerId: number;
  eventId: string;
  postNumber: number;
  title: string;
  author: string;
  publishedAt: string;
  displayStartAt?: string | null;
  // QA-481 — 노출 상태·예약 종료(BE 반영 전엔 없음).
  displayStatus?: 'ALWAYS' | 'SCHEDULED' | 'HIDDEN' | null;
  displayEndAt?: string | null;
}

export interface EventWinnerPage {
  rows: EventWinnerSummary[];
  total: number;
}

export const WINNER_PAGE_SIZE = 20;

interface PageEnvelope<T> extends Envelope<T> {
  meta?: { pagination?: { totalItems?: number } } | null;
}

/**
 * QA-407 — 관리자 당첨자 목록은 페이지(1-base)·요약으로 받는다(catalog #59).
 * page 없이 부르면 BE 가 본문 포함 전량을 주는데, 이관 글을 모은 숨김 캠페인은 수백 MB 라 반드시 page 를 보낸다.
 */
export const fetchWinnerPage = async (
  path: string,
  page: number,
  params: Record<string, string | undefined> = {},
): Promise<EventWinnerPage> => {
  const q = new URLSearchParams({ page: String(page), size: String(WINNER_PAGE_SIZE) });
  for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
  const res = await adminFetch(`${path}?${q.toString()}`);
  if (!res.ok) throw new Error(`event winners load failed: ${res.status}`);
  const env = (await res.json()) as PageEnvelope<EventWinnerSummary[]>;
  const rows = env.data ?? [];
  return { rows, total: env.meta?.pagination?.totalItems ?? rows.length };
};

export const eventWinnersQueryKey = (eventId: string) =>
  ['admin', 'event-winners', eventId] as const;

export const useEventWinnersQuery = (eventId: string, page = 1, keyword = '') =>
  useQuery<EventWinnerPage>({
    queryKey: [...eventWinnersQueryKey(eventId), page, keyword] as const,
    queryFn: () => fetchWinnerPage(base(eventId), page, { keyword: keyword.trim() || undefined }),
    enabled: !!eventId && eventId !== 'new',
    placeholderData: keepPreviousData,
  });

/** 단건(본문·번역 포함) — 편집 폼·본문 보기. */
export const fetchEventWinner = async (eventId: string, winnerId: number): Promise<EventWinnerView> => {
  const res = await adminFetch(`${base(eventId)}/${winnerId}`);
  if (!res.ok) throw new Error(`event winner load failed: ${res.status}`);
  const env = (await res.json()) as Envelope<EventWinnerView>;
  if (!env.data) throw new Error('event winner empty');
  return env.data;
};

export const useEventWinnerMutations = (eventId: string) => {
  const qc = useQueryClient();
  // 이벤트별·전체(QA-407) 목록과 단건 캐시를 함께 무효화한다.
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['admin', 'event-winners'] }),
      qc.invalidateQueries({ queryKey: ['admin', 'event-winner', eventId] }),
    ]);

  const create = useMutation({
    // QA-124 — 생성 후 "수정 모드" 전환을 위해 생성된 게시글(winnerId 포함)을 반환한다.
    mutationFn: async (body: EventWinnerRequest): Promise<EventWinnerView> => {
      const path = base(eventId);
      const res = await adminFetch(path, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', path, body) },
        body: JSON.stringify(body),
      });
      // 저장 실패는 BE error.code·message 를 살려 던진다(예: base64 이미지 400 사유를 그대로 안내).
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as Envelope<EventWinnerView>;
      if (!env.data) throw new Error('winner post create returned empty body');
      return env.data;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ winnerId, body }: { winnerId: number; body: EventWinnerRequest }): Promise<void> => {
      const path = `${base(eventId)}/${winnerId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await errorFromResponse(res);
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (winnerId: number): Promise<void> => {
      const path = `${base(eventId)}/${winnerId}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKeyFor('DELETE', path, '') },
      });
      if (!res.ok) throw new Error(`winner post delete failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
};
